import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { TextFormat, Translate, getSortState } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { APP_DATE_FORMAT, APP_LOCAL_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC } from 'app/shared/util/pagination.constants';
import { overrideSortStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './monthly-product-usage.reducer';

export const MonthlyProductUsage = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [sortState, setSortState] = useState(
    overrideSortStateWithQueryParams(getSortState(pageLocation, 'id'), pageLocation.search),
  );

  const monthlyProductUsageList = useAppSelector(state => state.monthlyProductUsage.entities);
  const loading = useAppSelector(state => state.monthlyProductUsage.loading);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        sort: `${sortState.sort},${sortState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?sort=${sortState.sort},${sortState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [sortState.order, sortState.sort]);

  const sort = p => () => {
    setSortState({
      ...sortState,
      order: sortState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = sortState.sort;
    const order = sortState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="monthly-product-usage-heading" data-cy="MonthlyProductUsageHeading">
        <Translate contentKey="crawlerBackendApp.monthlyProductUsage.home.title">Monthly Product Usages</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="crawlerBackendApp.monthlyProductUsage.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/monthly-product-usage/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="crawlerBackendApp.monthlyProductUsage.home.createLabel">
              Create new Monthly Product Usage
            </Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {monthlyProductUsageList && monthlyProductUsageList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="crawlerBackendApp.monthlyProductUsage.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('averagePrice')}>
                  <Translate contentKey="crawlerBackendApp.monthlyProductUsage.averagePrice">Average Price</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('averagePrice')} />
                </th>
                <th className="hand" onClick={sort('from')}>
                  <Translate contentKey="crawlerBackendApp.monthlyProductUsage.from">From</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('from')} />
                </th>
                <th className="hand" onClick={sort('until')}>
                  <Translate contentKey="crawlerBackendApp.monthlyProductUsage.until">Until</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('until')} />
                </th>
                <th className="hand" onClick={sort('monthlyUsage')}>
                  <Translate contentKey="crawlerBackendApp.monthlyProductUsage.monthlyUsage">Monthly Usage</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('monthlyUsage')} />
                </th>
                <th className="hand" onClick={sort('beginStand')}>
                  <Translate contentKey="crawlerBackendApp.monthlyProductUsage.beginStand">Begin Stand</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('beginStand')} />
                </th>
                <th className="hand" onClick={sort('endStand')}>
                  <Translate contentKey="crawlerBackendApp.monthlyProductUsage.endStand">End Stand</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('endStand')} />
                </th>
                <th>
                  <Translate contentKey="crawlerBackendApp.monthlyProductUsage.product">Product</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {monthlyProductUsageList.map((monthlyProductUsage, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/monthly-product-usage/${monthlyProductUsage.id}`} color="link" size="sm">
                      {monthlyProductUsage.id}
                    </Button>
                  </td>
                  <td>{monthlyProductUsage.averagePrice}</td>
                  <td>
                    {monthlyProductUsage.from ? (
                      <TextFormat type="date" value={monthlyProductUsage.from} format={APP_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {monthlyProductUsage.until ? (
                      <TextFormat type="date" value={monthlyProductUsage.until} format={APP_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>{monthlyProductUsage.monthlyUsage}</td>
                  <td>
                    {monthlyProductUsage.beginStand ? (
                      <TextFormat type="date" value={monthlyProductUsage.beginStand} format={APP_LOCAL_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {monthlyProductUsage.endStand ? (
                      <TextFormat type="date" value={monthlyProductUsage.endStand} format={APP_LOCAL_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {monthlyProductUsage.product ? (
                      <Link to={`/product/${monthlyProductUsage.product.id}`}>{monthlyProductUsage.product.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/monthly-product-usage/${monthlyProductUsage.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/monthly-product-usage/${monthlyProductUsage.id}/edit`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/monthly-product-usage/${monthlyProductUsage.id}/delete`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="crawlerBackendApp.monthlyProductUsage.home.notFound">
                No Monthly Product Usages found
              </Translate>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default MonthlyProductUsage;
